import { isAxiosError } from 'axios';
import { Exception, type ITelemetryItem } from '@microsoft/applicationinsights-web';

/**
 * Prefix marking a fingerprint as coming from the browser SPA, so it can never collide with a
 * backend ProblemId in the error-triage queries.
 */
const FINGERPRINT_PREFIX = 'spa';

/**
 * Context used when the caller did not supply one (e.g. uncaught exceptions).
 */
const UNHANDLED_CONTEXT = 'unhandled';

/**
 * Maximum length of the normalized message portion of a non-HTTP fingerprint.
 */
const MAX_MESSAGE_LENGTH = 100;

const GUID_PATTERN = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;
// Stripe-style IDs (`price_1Nx...`); the suffix must contain a digit or capital so snake_case words
// such as `product_registrations` are left alone.
const STRIPE_ID_PATTERN = /\b[a-z]+_(?=[A-Za-z0-9]*[A-Z0-9])[A-Za-z0-9]{8,}\b/g;
const DIGITS_PATTERN = /\d+/g;

/**
 * Replaces variable tokens (GUIDs, Stripe-style IDs, numbers) with placeholders so that
 * occurrences of the same error with different identifiers collapse into one group.
 * @param value - Route or message to normalize
 */
function normalizeVariableTokens(value: string): string {
  return value.replace(GUID_PATTERN, '{id}').replace(STRIPE_ID_PATTERN, '{id}').replace(DIGITS_PATTERN, '{n}');
}

/**
 * Reduces a request URL to a normalized route: drops the origin and query string, then replaces
 * variable path segments with placeholders.
 * @param url - Request URL as configured on the axios request (relative or absolute)
 */
function normalizeRoute(url: string | undefined): string {
  if (!url) {
    return '';
  }

  const withoutOrigin = url.replace(/^[a-zA-Z][a-zA-Z\d+.-]*:\/\/[^/]+/, '');
  const withoutQuery = withoutOrigin.split(/[?#]/)[0];
  return normalizeVariableTokens(withoutQuery);
}

/**
 * Builds a stable, build-independent fingerprint for an error raised in the SPA.
 * App Insights' built-in ProblemId is derived from minified stack frames, so it changes on every
 * deploy and lumps unrelated errors thrown from shared code together; this replaces it.
 *
 * - HTTP errors: `spa|<context>|<METHOD> <route> <status>` (`network` when there is no response).
 * - Other errors: `spa|<context>|<name>: <normalized message, max 100 chars>`.
 * @param error - The error to fingerprint
 * @param context - Logical location the error was reported from; defaults to `unhandled`
 */
export function buildErrorFingerprint(error: Pick<Error, 'name' | 'message'>, context?: string): string {
  const resolvedContext = context === undefined || context === '' ? UNHANDLED_CONTEXT : context;
  const prefix = `${FINGERPRINT_PREFIX}|${resolvedContext}|`;

  if (isAxiosError(error)) {
    const method = (error.config?.method ?? 'GET').toUpperCase();
    const route = normalizeRoute(error.config?.url);
    const status = error.response ? String(error.response.status) : 'network';
    return `${prefix}${method} ${route} ${status}`;
  }

  const message = normalizeVariableTokens(error.message).replace(/\s+/g, ' ').trim().slice(0, MAX_MESSAGE_LENGTH);
  return `${prefix}${error.name}: ${message}`;
}

/**
 * Whether the error is an HTTP 4xx response. These are handled client errors (validation,
 * not-found, forbidden) rather than defects, so they are logged as warnings instead of exceptions.
 * @param error - The error to inspect
 */
export function isClientHttpError(error: unknown): boolean {
  if (!isAxiosError(error)) {
    return false;
  }

  const status = error.response?.status;
  return status !== undefined && status >= 400 && status < 500;
}

/**
 * Telemetry initializer body for auto-collected (uncaught) exceptions: sets `data.fingerprint`
 * from the first exception's type name and message when no fingerprint is already present.
 * Exceptions logged through the logger already carry one in `baseData.properties` and are skipped.
 * @param item - Telemetry item about to be sent
 */
export function applyExceptionFingerprint(item: ITelemetryItem): void {
  if (item.baseType !== Exception.dataType) {
    return;
  }

  const properties = item.baseData?.properties as Record<string, unknown> | undefined;
  if (item.data?.fingerprint || properties?.fingerprint) {
    return;
  }

  const exceptions = item.baseData?.exceptions as { typeName?: string; message?: string }[] | undefined;
  const first = exceptions?.[0];
  if (!first) {
    return;
  }

  item.data ??= {};
  item.data.fingerprint = buildErrorFingerprint({ name: first.typeName ?? 'Error', message: first.message ?? '' });
}
